import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AuthLayout from "../../components/component/AuthLayout";
import { useResendEmailOtp, useVerifyEmailOtp } from "../../queries/auth";

const RESEND_COOLDOWN_SECONDS = 30;
const OTP_LENGTH = 6;

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email: string | undefined =
    location.state?.email || searchParams.get("email") || undefined;

  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const { mutateAsync: verifyOtp, isPending: isVerifying } = useVerifyEmailOtp();
  const { mutateAsync: resendOtp, isPending: isResending } = useResendEmailOtp();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // No email to verify — nothing useful to show here
  if (!email) {
    return <Navigate to="/signup" replace />;
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      toast.error(`Please enter the ${OTP_LENGTH}-digit code sent to your email.`);
      return;
    }
    await verifyOtp(
      { email, otp },
      {
        onSuccess: (data) => {
          toast.success(data.message || "Email verified successfully!", {
            position: "bottom-right",
            autoClose: 3000,
          });
          navigate("/login");
        },
      },
    );
  };

  const handleResend = async () => {
    await resendOtp(email, {
      onSuccess: () => setResendCooldown(RESEND_COOLDOWN_SECONDS),
    });
  };

  return (
    <AuthLayout showLogo={false}>
      <div>
        <h1 className="text-2xl font-bold mb-1">Verify Your Email</h1>
        <p className="text-gray-500 text-sm mb-5">
          We've sent a {OTP_LENGTH}-digit verification code to{" "}
          <span className="font-medium text-gray-700 dark:text-gray-200">{email}</span>.
          Enter it below to activate your account.
        </p>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label
              htmlFor="otp"
              className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
            >
              Verification Code
            </label>
            <input
              id="otp"
              name="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="w-full px-3 py-2.5 text-center text-2xl tracking-[0.5em] font-mono border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
              placeholder="••••••"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isVerifying || otp.length !== OTP_LENGTH}
          >
            {isVerifying ? "Verifying..." : "Verify Email"}
          </button>

          <div className="text-center text-xs text-gray-500 dark:text-gray-400">
            Didn't receive the code?{" "}
            {resendCooldown > 0 ? (
              <span>Resend available in {resendCooldown}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-primary hover:underline font-medium disabled:opacity-50"
              >
                {isResending ? "Sending..." : "Resend code"}
              </button>
            )}
          </div>

          <div className="text-center text-xs text-gray-500 dark:text-gray-400">
            Wrong email?{" "}
            <Link to="/signup" className="text-primary hover:underline font-medium">
              Sign up again
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
